// SPDX-License-Identifier: AGPL-3.0-or-later
/**
 * Stable string key for an `AnswerOption`. Used as the `<option>` `value`
 * and to dedupe selections — never serialized into the FHIR response.
 */
export function answerOptionKey(option) {
    if (option.valueCoding !== undefined) {
        const c = option.valueCoding;
        return `c:${c.system ?? ''}|${c.code ?? ''}`;
    }
    if (option.valueString !== undefined)
        return `s:${option.valueString}`;
    if (option.valueInteger !== undefined)
        return `i:${String(option.valueInteger)}`;
    if (option.valueDate !== undefined)
        return `d:${option.valueDate}`;
    if (option.valueTime !== undefined)
        return `t:${option.valueTime}`;
    if (option.valueReference?.reference !== undefined) {
        return `r:${option.valueReference.reference}`;
    }
    return '';
}
/** Human-readable label for an `AnswerOption`. */
export function answerOptionLabel(option) {
    if (option.valueCoding !== undefined) {
        return option.valueCoding.display ?? option.valueCoding.code ?? '';
    }
    if (option.valueString !== undefined)
        return option.valueString;
    if (option.valueInteger !== undefined)
        return String(option.valueInteger);
    if (option.valueDate !== undefined)
        return option.valueDate;
    if (option.valueTime !== undefined)
        return option.valueTime;
    if (option.valueReference !== undefined) {
        return option.valueReference.display ?? option.valueReference.reference ?? '';
    }
    return '';
}
/** Resolve a primitive or raw coding object to the full AnswerOption option from the choices. */
export function asAnswerOption(v, options) {
    if (v === null || v === undefined)
        return undefined;
    if (typeof v === 'object') {
        const obj = v;
        if ('valueCoding' in obj ||
            'valueString' in obj ||
            'valueInteger' in obj ||
            'valueDate' in obj ||
            'valueTime' in obj ||
            'valueReference' in obj) {
            return v;
        }
        // If it's a raw Coding object (e.g. system, code, display), find matching valueCoding in options
        if ('code' in obj || 'system' in obj) {
            return options.find((o) => {
                const c = o.valueCoding;
                return (c !== undefined &&
                    c.code === obj['code'] &&
                    (obj['system'] === undefined || c.system === obj['system']));
            });
        }
    }
    // If v is a primitive (e.g. string/number), find option matching that primitive
    return options.find((o) => {
        if (o.valueCoding !== undefined)
            return o.valueCoding.code === v;
        if (o.valueString !== undefined)
            return o.valueString === v;
        if (o.valueInteger !== undefined)
            return o.valueInteger === v;
        if (o.valueDate !== undefined)
            return o.valueDate === v;
        if (o.valueTime !== undefined)
            return o.valueTime === v;
        return false;
    });
}
/** Resolve a single value, array of primitives, or array of AnswerOptions to matching AnswerOptions[]. */
export function asAnswerOptionArray(v, options) {
    if (!Array.isArray(v)) {
        if (v !== undefined && v !== null) {
            const opt = asAnswerOption(v, options);
            return opt ? [opt] : [];
        }
        return [];
    }
    return v
        .map((item) => asAnswerOption(item, options))
        .filter((opt) => opt !== undefined);
}
//# sourceMappingURL=answerOption.js.map